/**
 * CampaignGroupingPage — operator console for labeled campaign groups.
 *
 * Layout: two-pane.
 *   LEFT sidebar  — list of groups with color swatch + member_count badge,
 *                   click to select, inline "+ New group" form.
 *   RIGHT main    — header for the selected group, table of campaigns in that
 *                   group with per-row remove, and an "Add Campaigns" dialog
 *                   that accepts a newline- or comma-separated list of
 *                   campaign IDs (MVP; drag-and-drop is stretch).
 *
 * Style follows apps/dash/DESIGN.md (emerald primary, zinc borders, 6-8px
 * radii, no orange/purple/emojis/dark-mode).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	DEFAULT_GROUP_COLOR,
	GROUP_COLOR_PALETTE,
	type GroupView,
} from "./api";
import {
	useCampaignGroupingAssign,
	useCampaignGroupingCampaigns,
	useCampaignGroupingCreate,
	useCampaignGroupingDelete,
	useCampaignGroupingList,
	useCampaignGroupingRemoveCampaign,
} from "./hooks";

const CARD = "bg-white border border-zinc-200 rounded-lg shadow-sm";
const INPUT =
	"w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400";
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const HELP = "text-xs text-zinc-500 mt-1";
const ERR = "text-xs text-red-600 mt-1";
const BTN_PRIMARY =
	"bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SECONDARY =
	"bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-md px-4 py-2 text-sm font-medium";
const BTN_DANGER =
	"bg-white border border-red-200 text-red-700 hover:bg-red-50 rounded-md px-3 py-1.5 text-xs font-medium";

function BadgeCount({ n }: { n: number }) {
	return (
		<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 tabular-nums">
			{n}
		</span>
	);
}

function ColorSwatch({ hex, size = 12 }: { hex: string; size?: number }) {
	return (
		<span
			aria-hidden
			data-testid="group-color-swatch"
			style={{ backgroundColor: hex, width: size, height: size }}
			className="inline-block rounded-full border border-zinc-200 shrink-0"
		/>
	);
}

function ColorPicker({
	value,
	onChange,
}: {
	value: string;
	onChange: (hex: string) => void;
}) {
	return (
		<div
			className="flex items-center gap-2"
			role="radiogroup"
			aria-label="Group color"
		>
			{GROUP_COLOR_PALETTE.map((c) => {
				const selected = c.value.toLowerCase() === value.toLowerCase();
				return (
					<button
						key={c.value}
						type="button"
						role="radio"
						aria-checked={selected}
						aria-label={c.label}
						onClick={() => onChange(c.value)}
						className={
							"w-7 h-7 rounded-full border flex items-center justify-center " +
							(selected
								? "border-emerald-500 ring-2 ring-emerald-500/30"
								: "border-zinc-200 hover:border-zinc-300")
						}
						data-testid={`group-color-${c.label.toLowerCase()}`}
					>
						<span
							className="w-5 h-5 rounded-full"
							style={{ backgroundColor: c.value }}
						/>
					</button>
				);
			})}
		</div>
	);
}

function NewGroupForm({
	onCreated,
}: {
	onCreated: (g: GroupView) => void;
}) {
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const [color, setColor] = useState<string>(DEFAULT_GROUP_COLOR);
	const createMut = useCampaignGroupingCreate();

	const reset = () => {
		setName("");
		setColor(DEFAULT_GROUP_COLOR);
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) return;
		createMut.mutate(
			{ name: trimmed, color },
			{
				onSuccess: (res) => {
					onCreated(res.group);
					setOpen(false);
					reset();
				},
			},
		);
	};

	if (!open) {
		return (
			<button
				type="button"
				className={BTN_PRIMARY + " w-full"}
				onClick={() => setOpen(true)}
				data-testid="new-group-open"
			>
				+ New group
			</button>
		);
	}
	return (
		<form
			onSubmit={handleSubmit}
			className="flex flex-col gap-3 p-3 border border-zinc-200 rounded-md bg-white"
			data-testid="new-group-form"
		>
			<div>
				<label className={LABEL} htmlFor="new-group-name">
					Group name
				</label>
				<input
					id="new-group-name"
					type="text"
					className={INPUT}
					placeholder="e.g. Q2 campaigns"
					value={name}
					onChange={(e) => setName(e.currentTarget.value)}
					maxLength={80}
					disabled={createMut.isPending}
					data-testid="new-group-name"
					autoFocus
				/>
			</div>
			<div>
				<label className={LABEL}>Color</label>
				<ColorPicker value={color} onChange={setColor} />
			</div>
			{createMut.error ? (
				<p className={ERR} role="alert" data-testid="new-group-error">
					{createMut.error.message}
				</p>
			) : null}
			<div className="flex justify-end gap-2">
				<button
					type="button"
					className={BTN_SECONDARY}
					onClick={() => {
						setOpen(false);
						reset();
					}}
					disabled={createMut.isPending}
				>
					Cancel
				</button>
				<button
					type="submit"
					className={BTN_PRIMARY}
					disabled={createMut.isPending || !name.trim()}
					data-testid="new-group-submit"
				>
					{createMut.isPending ? "Creating..." : "Create"}
				</button>
			</div>
		</form>
	);
}

function GroupSidebar({
	groups,
	selectedId,
	onSelect,
	isLoading,
	error,
	onCreated,
	onDelete,
}: {
	groups: GroupView[];
	selectedId: string | null;
	onSelect: (id: string) => void;
	isLoading: boolean;
	error: Error | null;
	onCreated: (g: GroupView) => void;
	onDelete: (id: string) => void;
}) {
	const deleteMut = useCampaignGroupingDelete();

	return (
		<aside
			className="w-72 shrink-0 border-r border-zinc-200 bg-zinc-50 flex flex-col"
			data-testid="group-sidebar"
		>
			<div className="p-4 border-b border-zinc-200">
				<h3 className="text-base font-medium text-zinc-900 mb-3">Groups</h3>
				<NewGroupForm onCreated={onCreated} />
			</div>
			<div className="flex-1 overflow-auto p-2">
				{isLoading ? (
					<p
						className="text-sm text-zinc-500 px-3 py-2"
						data-testid="campaign-grouping-loading"
					>
						Loading groups...
					</p>
				) : error ? (
					<p className="text-sm text-red-600 px-3 py-2" role="alert">
						{error.message}
					</p>
				) : groups.length === 0 ? (
					<p
						className="text-sm text-zinc-500 px-3 py-2"
						data-testid="campaign-grouping-empty"
					>
						No groups yet. Create one above.
					</p>
				) : (
					<ul
						className="flex flex-col gap-1"
						data-testid="campaign-grouping-list"
					>
						{groups.map((g) => {
							const selected = g.id === selectedId;
							return (
								<li key={g.id}>
									<div
										className={
											"group flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer " +
											(selected
												? "bg-emerald-50 text-emerald-700 border-l-2 border-emerald-500 font-medium"
												: "text-zinc-700 hover:bg-zinc-100")
										}
										onClick={() => onSelect(g.id)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault();
												onSelect(g.id);
											}
										}}
										role="button"
										tabIndex={0}
										aria-selected={selected}
										data-testid={`group-row-${g.id}`}
									>
										<ColorSwatch hex={g.color} />
										<span className="truncate flex-1" title={g.name}>
											{g.name}
										</span>
										<BadgeCount n={g.member_count} />
										<button
											type="button"
											className="opacity-0 group-hover:opacity-100 text-xs text-zinc-400 hover:text-red-600"
											onClick={(e) => {
												e.stopPropagation();
												if (
													window.confirm(
														`Delete group "${g.name}"? Members will be cleared.`,
													)
												) {
													deleteMut.mutate(g.id, {
														onSuccess: () => onDelete(g.id),
													});
												}
											}}
											aria-label={`Delete group ${g.name}`}
											data-testid={`group-delete-${g.id}`}
										>
											Delete
										</button>
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</aside>
	);
}

function AddCampaignsDialog({
	groupId,
	onClose,
}: {
	groupId: string;
	onClose: () => void;
}) {
	const [raw, setRaw] = useState("");
	const assignMut = useCampaignGroupingAssign();

	const parsed = useMemo(() => {
		return Array.from(
			new Set(
				raw
					.split(/[\n,]+/)
					.map((s) => s.trim())
					.filter(Boolean),
			),
		);
	}, [raw]);

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (parsed.length === 0) return;
		assignMut.mutate(
			{ groupId, campaignIds: parsed },
			{
				onSuccess: () => {
					onClose();
				},
			},
		);
	};

	return (
		<div
			className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
			onClick={onClose}
			data-testid="add-campaigns-overlay"
		>
			<div
				className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
				onClick={(e) => e.stopPropagation()}
				data-testid="add-campaigns-dialog"
				role="dialog"
				aria-labelledby="add-campaigns-title"
			>
				<h3
					id="add-campaigns-title"
					className="text-base font-medium text-zinc-900 mb-3"
				>
					Add campaigns to group
				</h3>
				<form onSubmit={handleSubmit} className="flex flex-col gap-3">
					<div>
						<label className={LABEL} htmlFor="add-campaigns-input">
							Campaign IDs
						</label>
						<textarea
							id="add-campaigns-input"
							className={INPUT + " min-h-[120px] font-mono text-xs"}
							placeholder="Paste campaign IDs, one per line or comma-separated"
							value={raw}
							onChange={(e) => setRaw(e.currentTarget.value)}
							disabled={assignMut.isPending}
							data-testid="add-campaigns-input"
							autoFocus
						/>
						<p className={HELP}>
							{parsed.length} unique {parsed.length === 1 ? "id" : "ids"}{" "}
							parsed.
						</p>
					</div>
					{assignMut.error ? (
						<p
							className={ERR}
							role="alert"
							data-testid="add-campaigns-error"
						>
							{assignMut.error.message}
						</p>
					) : null}
					<div className="flex justify-end gap-3">
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={onClose}
							disabled={assignMut.isPending}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={assignMut.isPending || parsed.length === 0}
							data-testid="add-campaigns-submit"
						>
							{assignMut.isPending
								? "Adding..."
								: `Add ${parsed.length || ""}`.trim()}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}

function GroupDetail({
	group,
}: {
	group: GroupView;
}) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const campaignsQuery = useCampaignGroupingCampaigns(group.id);
	const removeMut = useCampaignGroupingRemoveCampaign();

	const ids = campaignsQuery.data?.campaign_ids ?? [];

	return (
		<section
			className="flex-1 overflow-auto"
			data-testid={`group-detail-${group.id}`}
		>
			<div className="max-w-7xl mx-auto px-6 py-6">
				<header className="flex items-start justify-between mb-6">
					<div className="flex items-center gap-3">
						<ColorSwatch hex={group.color} size={20} />
						<div>
							<h2
								className="text-xl font-semibold text-zinc-900"
								data-testid="group-detail-name"
							>
								{group.name}
							</h2>
							<p className="text-sm text-zinc-500 mt-1">
								{group.member_count} campaign
								{group.member_count === 1 ? "" : "s"} in this group
							</p>
						</div>
					</div>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={() => setDialogOpen(true)}
						data-testid="open-add-campaigns"
					>
						Add campaigns
					</button>
				</header>

				<div className={CARD + " overflow-hidden"}>
					{campaignsQuery.isLoading ? (
						<p
							className="text-sm text-zinc-500 px-6 py-8 text-center"
							data-testid="campaigns-loading"
						>
							Loading campaigns...
						</p>
					) : campaignsQuery.error ? (
						<p
							className="text-sm text-red-600 px-6 py-8 text-center"
							role="alert"
						>
							{campaignsQuery.error.message}
						</p>
					) : ids.length === 0 ? (
						<div
							className="flex flex-col items-center justify-center py-12 px-6 text-center"
							data-testid="campaigns-empty"
						>
							<div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600 text-lg font-medium">
								0
							</div>
							<h3 className="text-lg font-semibold text-zinc-900 mt-4">
								No campaigns in this group
							</h3>
							<p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
								Add campaigns to this group to enable bulk operations and
								group-level reporting.
							</p>
							<button
								type="button"
								className={BTN_PRIMARY + " mt-4"}
								onClick={() => setDialogOpen(true)}
								data-testid="empty-add-campaigns"
							>
								Add campaigns
							</button>
						</div>
					) : (
						<table
							className="w-full border-collapse text-sm"
							data-testid="campaigns-table"
						>
							<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-4 py-3 text-left font-medium">
										Campaign ID
									</th>
									<th className="px-4 py-3 text-right font-medium w-32">
										Actions
									</th>
								</tr>
							</thead>
							<tbody>
								{ids.map((cid) => (
									<tr
										key={cid}
										className="hover:bg-emerald-50/40 transition-colors"
										data-testid={`campaign-row-${cid}`}
									>
										<td className="px-4 py-3 border-t border-zinc-200 font-mono text-xs text-zinc-700">
											{cid}
										</td>
										<td className="px-4 py-3 border-t border-zinc-200 text-right">
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() =>
													removeMut.mutate({
														groupId: group.id,
														campaignId: cid,
													})
												}
												disabled={removeMut.isPending}
												data-testid={`campaign-remove-${cid}`}
											>
												Remove
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>

			{dialogOpen ? (
				<AddCampaignsDialog
					groupId={group.id}
					onClose={() => setDialogOpen(false)}
				/>
			) : null}
		</section>
	);
}

export function CampaignGroupingPage() {
	const listQuery = useCampaignGroupingList();
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const groups = listQuery.data?.groups ?? [];
	const selectedGroup = useMemo(() => {
		if (!selectedId) return null;
		return groups.find((g) => g.id === selectedId) ?? null;
	}, [groups, selectedId]);

	return (
		<main
			data-testid="campaign-grouping-page"
			className="flex flex-col h-full w-full bg-white"
		>
			<div className="max-w-7xl mx-auto w-full px-6 pt-6 pb-4">
				<h1 className="text-2xl font-semibold bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">
					Campaign Groups
				</h1>
				<p className="text-sm text-zinc-500 mt-1">
					Organize campaigns into labeled groups for cleaner reporting and bulk
					operations.
				</p>
			</div>
			<div className="flex-1 flex border-t border-zinc-200 min-h-[500px]">
				<GroupSidebar
					groups={groups}
					selectedId={selectedId}
					onSelect={setSelectedId}
					isLoading={listQuery.isLoading}
					error={listQuery.error ?? null}
					onCreated={(g) => setSelectedId(g.id)}
					onDelete={(id) => {
						if (id === selectedId) setSelectedId(null);
					}}
				/>
				{selectedGroup ? (
					<GroupDetail group={selectedGroup} />
				) : (
					<section className="flex-1 flex items-center justify-center p-6">
						<div
							className="text-center max-w-sm"
							data-testid="no-group-selected"
						>
							<div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600 mx-auto">
								<span className="text-lg font-medium">G</span>
							</div>
							<h3 className="text-lg font-semibold text-zinc-900 mt-4">
								Select a group
							</h3>
							<p className="text-sm text-zinc-500 mt-2">
								Choose a group from the sidebar to see its campaigns, or create
								a new group to get started.
							</p>
						</div>
					</section>
				)}
			</div>
		</main>
	);
}

export default CampaignGroupingPage;
